<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { Topology } from '@/topo/core/topology'
import { ElMessage } from 'element-plus'
import DialogTopo from './dialog-topo.vue'

const props = defineProps({
  readOnly: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
})

const emit = defineEmits(['save'])

const containerRef = ref<HTMLElement | null>(null)
const dialogVisible = ref(false)
const dialogData = ref<Record<string, unknown>>({})
const propertyVisible = ref(true)
const showShortcuts = ref(false)
const canvasBg = ref('#eef1f6')
const showGrid = ref(false)

let topo: Topology | null = null
let gridInited = false

const shortcuts = [
  { keys: ['Ctrl', 'C'], desc: '复制选中元件' },
  { keys: ['Ctrl', 'V'], desc: '粘贴元件' },
  { keys: ['Delete'], desc: '删除选中元件' },
  { keys: ['Ctrl', 'Z'], desc: '撤销' },
  { keys: ['Ctrl', 'Y'], desc: '重做' },
]

onMounted(() => {
  initTopology()
})

onUnmounted(() => {
  topo?.removeEventListener('topo:openESS', eventHandler)
  topo = null
  // @ts-expect-error 调试挂载
  if (window.__topo) delete window.__topo
})

watch(canvasBg, (color) => {
  applyCanvasBackground(color)
})

watch(showGrid, (visible) => {
  applyGridVisible(visible)
})

function initTopology() {
  if (!containerRef.value) return

  topo = new Topology({
    root: containerRef.value,
    editor: !props.readOnly,
  })

  // @ts-expect-error 调试挂载
  window.__topo = topo

  applyCanvasBackground(canvasBg.value)
  applyGridVisible(showGrid.value)

  if (topo.storage) {
    topo.storage.gridColor = 'rgba(15, 23, 42, 0.08)'
    topo.storage.ruleColor = 'rgba(15, 23, 42, 0.25)'
  }

  topo.addEventListener('topo:openESS', eventHandler)
}

function applyCanvasBackground(color: string) {
  const graph = topo?.dom?.graphElem as HTMLElement | undefined
  if (graph) {
    graph.style.background = color
  }
}

function applyGridVisible(visible: boolean) {
  const storage = topo?.storage
  if (!storage?.layerGridAndRules) return

  if (visible && !gridInited) {
    storage.initGrid()
    gridInited = true
  }
  storage.layerGridAndRules.visible(visible)
  storage.layerGridAndRules.batchDraw()
}

function eventHandler(event: Event) {
  const custom = event as CustomEvent
  dialogVisible.value = true
  dialogData.value = (custom.detail as Record<string, unknown>) ?? {}
}

function onUndo() {
  topo?.undo()
}

function onRedo() {
  topo?.redo()
}

function onExport() {
  if (!topo) return
  const json = topo.toJSON()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' }))
  a.download = `topology_${Date.now()}.json`
  a.click()
  URL.revokeObjectURL(a.href)
  ElMessage.success('拓扑已导出')
}

function onImport() {
  if (!topo || props.readOnly) return
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string)
        topo?.clear()
        topo?.fromJSON(json)
        ElMessage.success('导入成功')
      } catch {
        ElMessage.error('JSON 文件格式无效')
      }
      input.remove()
    }
    reader.readAsText(file)
  }
  input.click()
}

function onSaveTopo() {
  if (!topo || props.saving) return
  const data = topo.toJSON()
  emit('save', data)
}

function togglePropertyPanel() {
  propertyVisible.value = !propertyVisible.value
}

function resetCanvas() {
  canvasBg.value = '#eef1f6'
  showGrid.value = false
}

function getTopologyJson() {
  return topo?.toJSON() ?? null
}

async function loadTopologyJson(data: unknown) {
  if (!topo || data == null) return
  await topo.fromJSON(data)
}

defineExpose({
  getTopologyJson,
  loadTopologyJson,
})
</script>

<template>
  <div
    class="topo-designer"
    :class="{
      'topo-designer--readonly': readOnly,
      'topo-designer--props-hidden': !propertyVisible,
    }"
  >
    <header v-if="!readOnly" class="topo-designer__toolbar">
      <div class="topo-designer__toolbar-left">
        <span class="topo-designer__brand">
          <v-icon size="18">mdi-sitemap-outline</v-icon>
          电站拓扑
        </span>
        <v-divider vertical />

        <div class="topo-designer__toolbar-group" role="group" aria-label="历史">
          <v-tooltip text="撤销 (Ctrl+Z)" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn v-bind="tipProps" icon variant="text" size="small" density="comfortable" @click="onUndo">
                <v-icon size="20">mdi-undo</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip text="重做 (Ctrl+Y)" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn v-bind="tipProps" icon variant="text" size="small" density="comfortable" @click="onRedo">
                <v-icon size="20">mdi-redo</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>

        <div class="topo-designer__toolbar-group" role="group" aria-label="文件">
          <v-tooltip text="导入 JSON" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn v-bind="tipProps" icon variant="text" size="small" density="comfortable" @click="onImport">
                <v-icon size="20">mdi-upload-outline</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip text="导出 JSON" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn v-bind="tipProps" icon variant="text" size="small" density="comfortable" @click="onExport">
                <v-icon size="20">mdi-download-outline</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <div class="topo-designer__toolbar-right">
        <div class="topo-designer__toolbar-group topo-designer__toolbar-group--ghost" role="group" aria-label="视图">
          <!-- <v-menu location="bottom end" :close-on-content-click="false">
            <template #activator="{ props: menuProps }">
              <v-btn v-bind="menuProps" variant="text" size="small" class="text-none">
                <v-icon start size="18">mdi-tune-variant</v-icon>
                画布设置
              </v-btn>
            </template>
            <v-card min-width="240" class="pa-3 topo-designer__settings-card">
              <div class="topo-designer__settings-label">背景颜色</div>
              <div class="topo-designer__settings-row">
                <input v-model="canvasBg" type="color" class="topo-designer__color-input" aria-label="画布背景色" />
                <v-text-field
                  v-model="canvasBg"
                  density="compact"
                  hide-details
                  variant="outlined"
                  rounded="lg"
                />
              </div>
              <div class="topo-designer__settings-label">辅助显示</div>
              <div class="topo-designer__settings-row">
                <v-switch
                  v-model="showGrid"
                  label="显示网格"
                  color="primary"
                  density="compact"
                  hide-details
                />
              </div>
              <v-btn
                variant="tonal"
                color="primary"
                size="small"
                rounded="lg"
                class="text-none topo-designer__settings-reset"
                @click="resetCanvas"
              >
                <v-icon start size="18">mdi-refresh</v-icon>
                重置画布
              </v-btn>
            </v-card>
          </v-menu> -->
          <!-- <v-tooltip :text="propertyVisible ? '隐藏属性面板' : '显示属性面板'" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                variant="text"
                size="small"
                class="text-none"
                @click="togglePropertyPanel"
              >
                <v-icon start size="18">
                  {{ propertyVisible ? 'mdi-dock-right' : 'mdi-dock-window' }}
                </v-icon>
                {{ propertyVisible ? '隐藏属性' : '显示属性' }}
              </v-btn>
            </template>
          </v-tooltip> -->
          <v-tooltip text="快捷键说明" location="bottom">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                icon
                variant="text"
                size="small"
                density="comfortable"
                :color="showShortcuts ? 'primary' : undefined"
                @click="showShortcuts = !showShortcuts"
              >
                <v-icon size="20">mdi-information-outline</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>

        <v-divider vertical />

        <v-btn
          color="primary"
          variant="flat"
          size="small"
          rounded="lg"
          class="text-none px-4 topo-designer__save-btn"
          :loading="saving"
          :disabled="saving"
          @click="onSaveTopo"
        >
          <v-icon start size="18">mdi-content-save-outline</v-icon>
          保存拓扑
        </v-btn>
      </div>
    </header>

    <div ref="containerRef" class="topo-designer__canvas-host" />

    <div v-if="showShortcuts && !readOnly" class="topo-designer__shortcuts" role="dialog" aria-label="快捷键提示">
      <div class="topo-designer__shortcuts-header">
        <p class="topo-designer__shortcuts-title">快捷键说明</p>
        <v-btn
          icon
          variant="text"
          size="x-small"
          density="comfortable"
          class="topo-designer__shortcuts-close"
          aria-label="关闭快捷键说明"
          @click="showShortcuts = false"
        >
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>
      <ul class="topo-designer__shortcuts-list">
        <li v-for="(item, i) in shortcuts" :key="i">
          <span class="topo-designer__key">
            <kbd v-for="(k, ki) in item.keys" :key="ki">{{ k }}</kbd>
          </span>
          <span>{{ item.desc }}</span>
        </li>
      </ul>
    </div>

    <v-dialog v-model="dialogVisible" title="储能系统设备" width="80%">
      <div class="topo-designer__dialog-body">
        <div class="topo-designer__dialog-header">
          <span class="topo-designer__dialog-title">
            <v-icon size="18">mdi-battery-charging-60</v-icon>
            储能系统设备
          </span>
          <v-btn
            icon
            variant="text"
            size="small"
            density="comfortable"
            class="topo-designer__dialog-close"
            aria-label="关闭"
            @click="dialogVisible = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
        <DialogTopo :dialog-data="dialogData" />
      </div>
    </v-dialog>
  </div>
</template>

<style lang="scss" src="@/topo/core/style.css"></style>
<style lang="scss" src="./topo-designer-light.scss"></style>

<style scoped lang="scss">
.topo-designer__dialog-body {
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(180deg, #f5f7fb 0%, #eef1f6 100%);
  border: 1px solid #e9efff;
  display: flex;
  flex-direction: column;
}

.topo-designer__dialog-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 12px 0 16px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: saturate(140%) blur(6px);
  -webkit-backdrop-filter: saturate(140%) blur(6px);
  border-bottom: 1px solid #e9efff;
}

.topo-designer__dialog-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: 0.02em;

  .v-icon {
    color: #2b57ee;
  }
}

.topo-designer__dialog-close {
  color: #9aa0b5;

  &:hover {
    color: #2b57ee;
    background: rgba(43, 87, 238, 0.08);
  }
}

.topo-designer__dialog-body :deep(.topo) {
  flex: 1;
  min-height: 0;
}

.topo-designer__color-input {
  width: 40px;
  height: 40px;
  padding: 2px;
  border: 1px solid #d1d9e6;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
}

.topo-designer__color-input::-webkit-color-swatch {
  border: none;
  border-radius: 6px;
}

.topo-designer__color-input::-webkit-color-swatch-wrapper {
  padding: 0;
}
</style>
